type AudienceOptions = {
	country?: { [key: string]: string };
	department?: { [key: string]: string };
	employment_type?: { [key: string]: string };
	location?: { [key: string]: string };
	role?: { [key: string]: string };
};

export enum CatalogState {
	Active = 'active',
	Paused = 'paused',
	Shutoff = 'shutoff',
}

type ChatIntegrations = {
	has_slack_integration: boolean;
	has_ms_teams_integration: boolean;
};

export enum ProductTier {
	TierFree = 'tier_free', // free plan
	TierOne = 'tier_one', // appreciate
	TierTwo = 'tier_two', // connect
	TierThree = 'tier_three', // Organization plan
	TierAlpha = 'tier_alpha', // Team plan
}

export enum UserMode {
	Benefactor = 'benefactor',
	Bot = 'bot',
	Normal = 'normal',
	Observer = 'observer',
	Receiver = 'receiver',
}

export enum PromotedRewardsSetting {
	BonuslyPromoted = 'bonusly_promoted',
	CompanyPromoted = 'company_promoted',
	Disabled = 'disabled',
}

export interface CompanySignUp {
	user_name: string | null;
	job_title: string | null;
	company_size: number;
	state: string;
}
export interface Company {
	id: string;
	active_user_count?: number;
	advanced_give_amounts_url: string | null;
	allow_auto_join: boolean;
	audience_options: AudienceOptions;
	auto_approve_max: number;
	automatic_user_management_enabled: boolean;
	axomo_subdomain?: string;
	banned_hashtags: string[];
	bonus_privacy: boolean;
	budget: {
		id: string;
		display_unit: string;
		exchange_rate: number;
		format: {
			singular: string;
			plural: string;
		};
		has_custom_currency: true;
		iso_code: string;
		plural: string;
		singular: string;
	};
	catalog_state: CatalogState;
	chat_integrations: ChatIntegrations;
	check_in_settings?: {
		mandatory: boolean;
		effective_date?: string;
	};
	company_hashtags: string[];
	core_values: Array<{ hashtag: string; description: string }>;
	created_at: string;
	currency_options: Array<{
		iso_code: string;
		name: string;
		symbol: string;
	}>;
	custom_theme?: {
		id: string;
		primary_color: string;
		secondary_color: string;
		accent_color: string;
	};
	custom_user_properties: string[];
	customize_give_amounts: boolean;
	disable_avatar_uploading: boolean;
	disable_gifs: boolean;
	disable_image_uploads: boolean;
	disable_video_recordings: boolean;
	disable_video_uploads: boolean;
	email_customizations: Record<string, Record<string, string>>;
	email_default_texts: Record<string, Record<string, string>>;
	example_reason: string;
	free_tier_user_limit?: number;
	give_amounts: number[];
	harvest_anniversary_dates: boolean;
	has_active_benefactors: boolean;
	has_celebrations: boolean;
	has_claimable_awards: boolean;
	has_sample_data: boolean;
	hashtagging_mode: string;
	hide_competitive_features: boolean;
	/**
	 * Whether this is the Bonusly company.
	 */
	is_bonusly_company?: boolean;
	is_demo: boolean;
	is_superadmin_initialized: boolean;
	join_url: string;
	/**
	 * Limited release mode for One-on-Ones features. When true, allows specific users
	 * to pilot One-on-Ones features via user-level flags while keeping it disabled
	 * company-wide. Set to false when ready to roll out One-on-Ones to the entire organization.
	 */
	limited_release_mode: boolean;
	locale: string;
	login_methods?: {
		google_oauth_enabled: boolean;
		microsoft_oauth_enabled: boolean;
		saml_enabled: boolean;
	};
	logo_url: string;
	monarch_cohort: string;
	multi_factor_auth_enforced_at: string | null;
	multi_factor_auth_required: boolean;
	name: string;
	next_billing_at: string;
	product_plan_name: string;
	product_tier: ProductTier;
	promoted_rewards_setting: PromotedRewardsSetting;
	reward_catalog_auto_add_items: boolean;
	/**
	 * Whether rewards billing is enabled for this company.
	 * When false, users can only redeem no-cost rewards (Points Boost, Exclusives, Swag).
	 * When true or undefined (for backwards compatibility), all rewards are available.
	 */
	rewards_billing_enabled?: boolean;
	rewards_billing_plan?: string;
	send_activity_digest_emails: boolean;
	send_bonus_emails: boolean;
	send_manager_digest_emails: boolean;
	send_pending_reward_emails: boolean;
	send_reminder_emails: boolean;
	send_welcome_emails: boolean;
	settings_integrations_url: string;
	show_bonus_amounts: boolean;
	sign_up: CompanySignUp;
	sign_up_company_size: number;
	single_sign_on_enabled: boolean;
	status: string;
	subscription_days_past_due: number;
	suggested_hashtags: string[];
	tags: string[];
	time_zone: string;
	trending_hashtags: string[];
	trial_end_date: string;
	trial_start_date: string;
	updated_at: string;
	user_earnings_cap: number | null;
	user_management_integration_url: string;
	zero_point_bonuses: boolean;
}
